import { memo, useCallback, useMemo, useState } from "react"
import dayjs from "dayjs"
import ChevronLeftIcon from "@/shared/icons/totodoicons/stroke/chevron-left.svg?react"
import ChevronRightIcon from "@/shared/icons/totodoicons/stroke/chevron-right.svg?react"
import { cn } from "@/shared/utils/general/cn"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MiniCalendarProps {
  selectedDate?: Date
  onSelect: (date: Date) => void
  /** Minimum selectable date (defaults to today) */
  minDate?: Date
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Minimal month calendar for date selection.
 * Shows a single month with navigation arrows.
 * Only future dates (from today onward) are selectable.
 */
export const MiniCalendar = memo(function MiniCalendar({
  selectedDate,
  onSelect,
  minDate,
}: MiniCalendarProps) {
  const today = useMemo(() => dayjs().startOf("day"), [])
  const min = useMemo(
    () => (minDate ? dayjs(minDate).startOf("day") : today),
    [minDate, today],
  )

  // Start displaying from the month containing the selected date or today
  const [viewMonth, setViewMonth] = useState(() =>
    selectedDate ? dayjs(selectedDate).startOf("month") : today.startOf("month"),
  )

  const prevMonth = useCallback(() => {
    setViewMonth((m) => m.subtract(1, "month"))
  }, [])

  const nextMonth = useCallback(() => {
    setViewMonth((m) => m.add(1, "month"))
  }, [])

  // Build calendar grid
  const { weeks, monthLabel } = useMemo(() => {
    const startOfMonth = viewMonth.startOf("month")
    const endOfMonth = viewMonth.endOf("month")

    // Monday-based week start (ISO)
    let startDay = startOfMonth.day() - 1
    if (startDay < 0) startDay = 6 // Sunday → last

    const totalDays = endOfMonth.date()
    const cells: Array<{
      date: dayjs.Dayjs
      inMonth: boolean
      isToday: boolean
      isSelected: boolean
      isDisabled: boolean
    }> = []

    // Previous month padding
    for (let i = startDay - 1; i >= 0; i--) {
      const d = startOfMonth.subtract(i + 1, "day")
      cells.push({
        date: d,
        inMonth: false,
        isToday: false,
        isSelected: false,
        isDisabled: true,
      })
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const d = startOfMonth.date(day)
      const isDisabled = d.isBefore(min, "day")
      cells.push({
        date: d,
        inMonth: true,
        isToday: d.isSame(today, "day"),
        isSelected: selectedDate
          ? d.isSame(dayjs(selectedDate), "day")
          : false,
        isDisabled,
      })
    }

    // Next month padding to fill last row
    const remainder = cells.length % 7
    if (remainder > 0) {
      for (let i = 1; i <= 7 - remainder; i++) {
        const d = endOfMonth.add(i, "day")
        cells.push({
          date: d,
          inMonth: false,
          isToday: false,
          isSelected: false,
          isDisabled: true,
        })
      }
    }

    // Group into weeks
    const weeks: typeof cells[] = []
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7))
    }

    return {
      weeks,
      monthLabel: viewMonth.format("MMMM YYYY"),
    }
  }, [viewMonth, today, min, selectedDate])

  const canGoPrev = viewMonth.isAfter(min.startOf("month"))

  return (
    <div className="select-none">
      {/* Month navigation */}
      <div className="flex items-center justify-between py-1">
        <button
          onClick={prevMonth}
          disabled={!canGoPrev}
          className={cn(
            "rounded p-1 transition-colors",
            canGoPrev
              ? "text-muted-foreground hover:bg-accent hover:text-foreground"
              : "text-muted-foreground/30 cursor-not-allowed",
          )}
        >
          <ChevronLeftIcon className="size-4" />
        </button>
        <span className="text-xs font-semibold">{monthLabel}</span>
        <button
          onClick={nextMonth}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ChevronRightIcon className="size-4" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-0">
        {WEEKDAYS.map((wd) => (
          <div
            key={wd}
            className="flex items-center justify-center py-1 text-[10px] font-medium text-muted-foreground/60"
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0">
        {weeks.flat().map((cell, i) => {
          const key = cell.date.format("YYYY-MM-DD")
          return (
            <button
              key={`${key}-${i}`}
              disabled={cell.isDisabled}
              onClick={() => {
                if (!cell.isDisabled) {
                  onSelect(cell.date.toDate())
                }
              }}
              className={cn(
                "flex aspect-square items-center justify-center rounded text-xs",
                "transition-colors",
                cell.isDisabled && "cursor-default text-muted-foreground/20",
                !cell.isDisabled &&
                  !cell.isSelected &&
                  cell.inMonth &&
                  "text-foreground hover:bg-accent",
                !cell.isDisabled &&
                  !cell.isSelected &&
                  !cell.inMonth &&
                  "text-muted-foreground/40 hover:bg-accent/50",
                cell.isToday &&
                  !cell.isSelected &&
                  "font-bold text-primary",
                cell.isSelected &&
                  "bg-primary text-primary-foreground font-semibold",
              )}
            >
              {cell.date.date()}
            </button>
          )
        })}
      </div>
    </div>
  )
})
